import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Search,
  Download,
  Eye,
  CheckCircle,
  Plus,
  X,
  User,
  Receipt,
  Trash2,
  AlertTriangle,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Upload
} from 'lucide-react';
import BulkUploadModal from '../../../components/common/BulkUploadModal';
import Button from '../../../components/common/Button/Button.jsx';
import PermissionGate from '../../../components/common/PermissionGate/PermissionGate';
import jsPDF from 'jspdf';
import { toast } from '../../../utils/toast';
import fullFinalSettlementApi from '../../../api/fullFinalSettlementApi';
import { formatDate } from '../../../utils/stringUtils';
import { formatIndianNumber } from '../../../utils/amountFormatter';
import { API_BASE_URL } from '../../../api/config';
import ExportModal from '../../../components/common/ExportModal';
import { generateExportFilename, downloadFromBackend, exportToCSV } from '../../../utils/exportUtils';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import PaginationControls from '../../../components/common/Pagination/PaginationControls';
import useUrlTableState from '../../../hooks/useUrlTableState';

const FullFinalSettlement = () => {
  const defaultFilters = useMemo(() => ({
    page: 1,
    limit: 10,
    search: '',
    filterStatus: 'all',
    filterStartDate: '',
    filterEndDate: ''
  }), []);

  const {
    filters: settlementFilters,
    searchTerm: searchQuery,
    setSearchTerm: setSearchQuery,
    updateFilter: updateSettlementFilter,
    updatePage: updateSettlementPage,
    updateLimit: updateSettlementLimit,
    resetFilters: resetSettlementFilters,
  } = useUrlTableState(defaultFilters);

  const searchTerm = searchQuery;
  const setSearchTerm = setSearchQuery;

  const filterStatus = settlementFilters.filterStatus || 'all';
  const setFilterStatus = useCallback((status) => {
    updateSettlementFilter({ filterStatus: status });
  }, [updateSettlementFilter]);

  const startDate = settlementFilters.filterStartDate || '';
  const endDate = settlementFilters.filterEndDate || '';

  const setDateRange = useCallback((range) => {
    updateSettlementFilter({ filterStartDate: range.startDate || '', filterEndDate: range.endDate || '' });
  }, [updateSettlementFilter]);

  const setStartDate = useCallback((date) => {
    updateSettlementFilter({ filterStartDate: date });
  }, [updateSettlementFilter]);

  const setEndDate = useCallback((date) => {
    updateSettlementFilter({ filterEndDate: date });
  }, [updateSettlementFilter]);

  const currentPage = settlementFilters.page;
  const itemsPerPage = settlementFilters.limit;

  const setCurrentPage = useCallback((page) => {
    updateSettlementPage(page);
  }, [updateSettlementPage]);

  const setItemsPerPage = useCallback((limit) => {
    updateSettlementLimit(limit);
  }, [updateSettlementLimit]);

  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');
  const [showSettlementModal, setShowSettlementModal] = useState(false);
  const [selectedSettlement, setSelectedSettlement] = useState(null);
  const [activeTab, setActiveTab] = useState('settlements');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [settlementToDelete, setSettlementToDelete] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [settlementToApprove, setSettlementToApprove] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);

  useEffect(() => {
    setSelectedRows(new Set());
  }, [searchTerm, filterStatus]);

  // Page reset is handled by useUrlTableState

  const [selectedEmployeeDetails, setSelectedEmployeeDetails] = useState(null);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [formSubmitted, setFormSubmitted] = useState(false);
  const queryClient = useQueryClient();

  const MAX_AMOUNT = 1000000000;

  const formatDateInput = (dateValue) => {
    if (!dateValue) return '';
    const parsed = new Date(dateValue);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toISOString().split('T')[0];
  };

  const formatDisplayDate = (value, options = { includeTime: false }) => {
    const formatted = formatDate(value, options);
    return formatted || 'N/A';
  };

  const validateApiResponse = (response, fallbackMessage, allowedStatuses = [200]) => {
    const statusCode = Number(response?.responseCode);
    if (Number.isFinite(statusCode) && !allowedStatuses.includes(statusCode)) {
      throw new Error(response?.responseMessage || fallbackMessage);
    }
    return response;
  };


  const calculateTotal = () => {
    const components = formData.components;
    const earnings =
      (parseFloat(components.salaryTillLWD) || 0) +
      (parseFloat(components.leaveEncashment) || 0) +
      (parseFloat(components.bonus) || 0) +
      (parseFloat(components.gratuity) || 0) +
      (parseFloat(components.otherAllowances) || 0);

    const deductions =
      (parseFloat(components.deductions.advanceRecovery) || 0) +
      (parseFloat(components.deductions.loanRecovery) || 0) +
      (parseFloat(components.deductions.otherDeductions) || 0);

    return {
      earnings,
      deductions,
      netPayable: earnings - deductions,
    };
  };

  // Handle key press for text inputs (alphanumeric, spaces, commas only)
  const handleTextKeyPress = (e) => {
    if (!/^[a-zA-Z0-9\s,]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.preventDefault();
    }
  };

  // Handle key down for number inputs (prevent negative signs and decimals)
  const handleNumberKeyDown = (e) => {
    if (e.key === '-' || e.key === '.' || e.key === 'e' || e.key === 'E') {
      e.preventDefault();
    }
  };

  // Helper function to handle number input changes
  const sanitizeNumericInput = (value, max = MAX_AMOUNT) => {
    if (!value) return '';
    let sanitized = value.replace(/\D/g, '');
    if (!sanitized) return '';
    const parsed = parseInt(sanitized, 10);
    if (Number.isNaN(parsed)) return '';
    return String(Math.min(parsed, max));
  };

  const validateSettlementForm = useCallback((data) => {
    const errors = {};
    if (!data.employeeId || !data.employeeId.trim()) {
      errors.employeeId = "Please select an employee.";
    }
    if (!data.lastWorkingDate || !data.lastWorkingDate.trim()) {
      errors.lastWorkingDate = "Last working date is required.";
    }
    if (!data.resignationDate || !data.resignationDate.trim()) {
      errors.resignationDate = "Resignation date is required.";
    }

    const components = data.components || {};
    const deductions = components.deductions || {};

    if (!components.salaryTillLWD && components.salaryTillLWD !== 0) {
      errors.salaryTillLWD = "Field is required.";
    }
    if (!components.leaveEncashment && components.leaveEncashment !== 0) {
      errors.leaveEncashment = "Field is required.";
    }
    if (!components.bonus && components.bonus !== 0) {
      errors.bonus = "Field is required.";
    }
    if (!components.gratuity && components.gratuity !== 0) {
      errors.gratuity = "Field is required.";
    }
    if (!components.otherAllowances && components.otherAllowances !== 0) {
      errors.otherAllowances = "Field is required.";
    }
    if (!deductions.advanceRecovery && deductions.advanceRecovery !== 0) {
      errors.advanceRecovery = "Field is required.";
    }
    if (!deductions.loanRecovery && deductions.loanRecovery !== 0) {
      errors.loanRecovery = "Field is required.";
    }
    if (!deductions.otherDeductions && deductions.otherDeductions !== 0) {
      errors.otherDeductions = "Field is required.";
    }

    return errors;
  }, []);

  const validateField = (fieldName, value) => {
    setTouched(prev => ({ ...prev, [fieldName]: true }));
    const formErrors = validateSettlementForm({ ...formData, [fieldName]: value });
    setErrors(prev => ({ ...prev, [fieldName]: formErrors[fieldName] }));
  };

  const validateAllFields = () => {
    const formErrors = validateSettlementForm(formData);
    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  const resetFormFields = useCallback(() => {
    const initialData = getInitialFormData();
    setFormData(initialData);
    setSelectedEmployeeDetails(null);
    setErrors({});
    setTouched({});
    setFormSubmitted(false);
    if (employeeIdRef.current) employeeIdRef.current.value = '';
    if (lastWorkingDateRef.current) lastWorkingDateRef.current.value = initialData.lastWorkingDate;
    if (resignationDateRef.current) resignationDateRef.current.value = initialData.resignationDate;
    if (salaryTillLWDRef.current) salaryTillLWDRef.current.value = initialData.components.salaryTillLWD;
    if (leaveEncashmentRef.current) leaveEncashmentRef.current.value = initialData.components.leaveEncashment;
    if (bonusRef.current) bonusRef.current.value = initialData.components.bonus;
    if (gratuityRef.current) gratuityRef.current.value = initialData.components.gratuity;
    if (otherAllowancesRef.current) otherAllowancesRef.current.value = initialData.components.otherAllowances;
    if (advanceRecoveryRef.current) advanceRecoveryRef.current.value = initialData.components.deductions.advanceRecovery;
    if (loanRecoveryRef.current) loanRecoveryRef.current.value = initialData.components.deductions.loanRecovery;
    if (otherDeductionsRef.current) otherDeductionsRef.current.value = initialData.components.deductions.otherDeductions;
  }, []);

  const handleEmployeeSelection = (employeeCode) => {
    if (!employeeCode) {
      setSelectedEmployeeDetails(null);
      setFormData((prev) => ({
        ...prev,
        employeeId: '',
        lastWorkingDate: '',
        resignationDate: '',
      }));
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.employeeId;
        delete newErrors.lastWorkingDate;
        delete newErrors.resignationDate;
        return newErrors;
      });
      if (employeeIdRef.current) employeeIdRef.current.value = '';
      if (lastWorkingDateRef.current) lastWorkingDateRef.current.value = '';
      if (resignationDateRef.current) resignationDateRef.current.value = '';
      return;
    }

    const selected = approvedEmployees.find((emp) => emp.employeeIdValue === employeeCode) || null;
    setSelectedEmployeeDetails(selected);
    const formattedLastWorking = formatDateInput(selected?.lastWorkingDate);
    const formattedResignation = formatDateInput(selected?.resignationDate);

    if (employeeIdRef.current) employeeIdRef.current.value = employeeCode;
    if (lastWorkingDateRef.current) lastWorkingDateRef.current.value = formattedLastWorking;
    if (resignationDateRef.current) resignationDateRef.current.value = formattedResignation;

    setFormData((prev) => ({
      ...prev,
      employeeId: employeeCode,
      lastWorkingDate: formattedLastWorking,
      resignationDate: formattedResignation,
    }));

    // Clear validation errors for these fields when employee is selected
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.employeeId;
      delete newErrors.lastWorkingDate;
      delete newErrors.resignationDate;
      return newErrors;
    });
    setTouched(prev => ({
      ...prev,
      employeeId: true,
      lastWorkingDate: true,
      resignationDate: true
    }));
  };

  // Create refs for all input fields
  const employeeIdRef = useRef(null);
  const lastWorkingDateRef = useRef(null);
  const resignationDateRef = useRef(null);
  const salaryTillLWDRef = useRef(null);
  const leaveEncashmentRef = useRef(null);
  const bonusRef = useRef(null);
  const gratuityRef = useRef(null);
  const otherAllowancesRef = useRef(null);
  const advanceRecoveryRef = useRef(null);
  const loanRecoveryRef = useRef(null);
  const otherDeductionsRef = useRef(null);

  const getInitialFormData = () => ({
    employeeId: '',
    lastWorkingDate: '',
    resignationDate: '',
    components: {
      salaryTillLWD: '',
      leaveEncashment: '',
      bonus: '',
      gratuity: '',
      otherAllowances: '',
      deductions: {
        advanceRecovery: '',
        loanRecovery: '',
        otherDeductions: '',
      },
    },
  });

  const [formData, setFormData] = useState(getInitialFormData);



  const {
    data: approvedEmployeesData = [],
    isFetching: isLoadingEmployees,
  } = useQuery({
    queryKey: ['approved-exit-employees'],
    queryFn: async () => {
      const url = `${API_BASE_URL}/exit-test?status=approved&limit=1000`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Failed to load approved employees');
      }

      const result = await response.json();
      const exitRequests = result?.data?.exitRequests || result?.data || [];

      return exitRequests
        .map((request) => {
          const employee = request.employeeId || {};
          const firstName = employee.personalInfo?.firstName || employee.firstName || '';
          const lastName = employee.personalInfo?.lastName || employee.lastName || '';
          const fullName = (request.employeeName || `${firstName} ${lastName}`.trim() || employee.fullName || '').trim();
          const employeeCode = employee.employeeId || employee.employeeCode || employee._id || request.employeeId || request.employeeCode;

          if (!employeeCode) {
            return null;
          }

          const department = request.department || employee.employmentInfo?.department || employee.department || 'Unknown';
          return {
            id: request._id || employeeCode,
            employeeIdValue: employeeCode,
            employeeName: fullName || employeeCode,
            department,
            displayLabel: `${fullName || employeeCode} (${employeeCode})${department ? ` • ${department}` : ''}`,
            lastWorkingDate: request.lastWorkingDate,
            resignationDate: request.resignationDate || request.appliedDate || request.createdAt,
            noticePeriod: request.noticePeriod?.toString() || request.noticePeriodDays?.toString() || '',
            exitRequestId: request._id || null,
          };
        })
        .filter(Boolean);
    },
    enabled: showSettlementModal && !selectedSettlement,
    staleTime: 0,
    refetchOnWindowFocus: false,
    onError: (error) => {
      toast.error(error?.message || 'Failed to load approved employees');
    },
  });

  const approvedEmployees = approvedEmployeesData || [];

  const createSettlementMutation = useMutation({
    mutationFn: async (payload) => {
      const response = await fullFinalSettlementApi.createSettlement(payload);
      return validateApiResponse(response, 'Failed to create settlement', [200, 201]);
    },
    onSuccess: () => {
      toast.success('Settlement created successfully!');
      setShowSettlementModal(false);
      setSelectedSettlement(null);
      resetFormFields();
      queryClient.invalidateQueries({ queryKey: ['fullFinalSettlements'] });
    },
    onError: (error) => {
      toast.error(error?.message || 'Error creating settlement');
    },
  });

  const approveSettlementMutation = useMutation({
    mutationFn: async (settlementId) => {
      const response = await fullFinalSettlementApi.approveSettlement(settlementId);
      return validateApiResponse(response, 'Failed to approve settlement', [200]);
    },
    onSuccess: () => {
      toast.success('Settlement approved successfully!');
      setShowApproveModal(false);
      setSettlementToApprove(null);
      queryClient.invalidateQueries({ queryKey: ['fullFinalSettlements'] });
    },
    onError: (error) => {
      toast.error(error?.message || 'Error approving settlement');
    },
  });

  const deleteSettlementMutation = useMutation({
    mutationFn: async (settlementId) => {
      const response = await fullFinalSettlementApi.deleteSettlement(settlementId);
      return validateApiResponse(response, 'Failed to delete settlement', [200]);
    },
    onSuccess: () => {
      toast.success('Settlement deleted successfully!');
      setShowDeleteModal(false);
      setSettlementToDelete(null);
      queryClient.invalidateQueries({ queryKey: ['fullFinalSettlements'] });
    },
    onError: (error) => {
      toast.error(error?.message || 'Error deleting settlement');
    },
  });

  const isCreatingSettlement = createSettlementMutation.isPending;
  const isApprovingSettlement = approveSettlementMutation.isPending;
  const isDeletingSettlement = deleteSettlementMutation.isPending;

  useEffect(() => {
    if (showSettlementModal) {
      document.body.classList.add('modal-open');
      // Reset form and validation errors when opening modal for new settlement
      if (!selectedSettlement) {
        resetFormFields();
      }
    } else {
      document.body.classList.remove('modal-open');
      resetFormFields();
    }
    return () => document.body.classList.remove('modal-open');
  }, [showSettlementModal, selectedSettlement, resetFormFields]);

  useEffect(() => {
    if (showDeleteModal) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    return () => document.body.classList.remove('modal-open');
  }, [showDeleteModal]);

  useEffect(() => {
    if (showApproveModal) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    return () => document.body.classList.remove('modal-open');
  }, [showApproveModal]);

  // Form input handlers - using refs to prevent re-render issues
  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      return newData;
    });
  };

  const handleComponentChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      components: {
        ...prev.components,
        [field]: value
      }
    }));
  };


  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleDownloadSettlement = (settlement) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 12;
    let yPos = margin;

    // Helper function to add text with styling
    const addText = (text, x, y, fontSize = 12, isBold = false, color = { r: 0, g: 0, b: 0 }) => {
      doc.setFontSize(fontSize);
      doc.setFont('helvetica', isBold ? 'bold' : 'normal');
      doc.setTextColor(color.r, color.g, color.b);
      doc.text(text, x, y);
    };

    // Helper function to add right-aligned text
    const addTextRight = (text, x, y, fontSize = 12, isBold = false, color = { r: 0, g: 0, b: 0 }) => {
      doc.setFontSize(fontSize);
      doc.setFont('helvetica', isBold ? 'bold' : 'normal');
      doc.setTextColor(color.r, color.g, color.b);
      const textWidth = doc.getTextWidth(text);
      doc.text(text, x - textWidth, y);
    };

    // Helper function to add center-aligned text
    const addTextCenter = (text, x, y, fontSize = 12, isBold = false, color = { r: 0, g: 0, b: 0 }) => {
      doc.setFontSize(fontSize);
      doc.setFont('helvetica', isBold ? 'bold' : 'normal');
      doc.setTextColor(color.r, color.g, color.b);
      const textWidth = doc.getTextWidth(text);
      doc.text(text, x - textWidth / 2, y);
    };

    // Helper function to draw a line
    const drawLine = (y, lineWidth = 0.3) => {
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(lineWidth);
      doc.line(margin, y, pageWidth - margin, y);
    };

    // Header Section
    yPos += 3;
    addTextCenter('FULL & FINAL SETTLEMENT', pageWidth / 2, yPos, 18, true, { r: 0, g: 0, b: 0 });
    yPos += 6;

    // Date and Settlement ID - Right aligned
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(120, 120, 120);
    const currentDate = new Date().toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
    addTextRight(`Generated: ${currentDate}`, pageWidth - margin, yPos, 8, false);
    yPos += 4;
    yPos += 8;

    // Employee Information Section
    drawLine(yPos);
    yPos += 6;
    addText('Employee Information', margin, yPos, 11, true, { r: 0, g: 0, b: 0 });
    yPos += 7;

    // Employee details - Compact two column layout
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);

    const empData = [
      { label: 'Employee Name', value: settlement.employeeName || 'N/A', wrap: true },
      { label: 'Department', value: settlement.department || 'N/A', wrap: true },
      { label: 'Employee ID', value: settlement.employeeId || 'N/A', wrap: false },
      { label: 'Last Working Date', value: formatDisplayDate(settlement.lastWorkingDate), wrap: false },
    ];

    const col1X = margin + 3;
    const col2X = pageWidth / 2 + 3;
    let rowY = yPos;
    let maxRowHeight = 0; // Track maximum height for current row

    // Calculate max label width to ensure consistent spacing
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    const maxLabelWidth = Math.max(...empData.map(item => doc.getTextWidth(`${item.label}:`)));
    const spacing = 6; // 18px spacing between label and value
    const valueMaxWidth = (pageWidth / 2) - col1X - maxLabelWidth - spacing - 10; // Max width for wrapped values

    empData.forEach((item, index) => {
      const xPos = index % 2 === 0 ? col1X : col2X;
      const isLeftColumn = index % 2 === 0;

      // Move to next row when starting a new left column item
      if (isLeftColumn && index > 0) {
        rowY += Math.max(6, maxRowHeight); // Use max height from previous row
        maxRowHeight = 0; // Reset for new row
      }

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text(`${item.label}:`, xPos, rowY);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(0, 0, 0);
      const valueX = xPos + maxLabelWidth + spacing;

      let itemHeight = 6; // Default row height
      if (item.wrap) {
        // Wrap text for name and department
        const valueLines = doc.splitTextToSize(item.value, valueMaxWidth);
        doc.text(valueLines, valueX, rowY);
        // Calculate height based on number of lines
        itemHeight = Math.max(6, valueLines.length * 4);
      } else {
        doc.text(item.value, valueX, rowY);
      }

      // Track maximum height for current row (for left/right column pairs)
      maxRowHeight = Math.max(maxRowHeight, itemHeight);
    });

    yPos = rowY + maxRowHeight + 2; // Add final row height plus spacing

    // Earnings Section
    drawLine(yPos);
    yPos += 6;
    addText('Earnings', margin, yPos, 11, true, { r: 0, g: 0, b: 0 });
    yPos += 7;

    // Earnings Table Header
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.3);
    doc.setFillColor(248, 248, 248);
    doc.rect(margin, yPos - 3, pageWidth - 2 * margin, 6, 'FD');
    addText('Description', margin + 3, yPos + 1, 9, true, { r: 0, g: 0, b: 0 });
    addTextRight('Amount', pageWidth - margin - 3, yPos + 1, 9, true, { r: 0, g: 0, b: 0 });
    yPos += 8;

    // Earnings Table Rows
    const earnings = [
      { label: 'Salary till Last Working Date', amount: settlement.components.salaryTillLWD || 0 },
      { label: 'Leave Encashment', amount: settlement.components.leaveEncashment || 0 },
      { label: 'Bonus', amount: settlement.components.bonus || 0 },
      { label: 'Gratuity', amount: settlement.components.gratuity || 0 },
      { label: 'Other Allowances', amount: settlement.components.otherAllowances || 0 },
    ];

    earnings.forEach((item, index) => {
      const rowHeight = 6;
      const rowY = yPos;

      // Subtle background for alternating rows
      if (index % 2 === 1) {
        doc.setFillColor(252, 252, 252);
        doc.rect(margin, rowY - 2, pageWidth - 2 * margin, rowHeight, 'F');
      }

      // Description with proper wrapping
      const descriptionMaxWidth = (pageWidth - 2 * margin) - 75;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(0, 0, 0);
      const lines = doc.splitTextToSize(item.label, descriptionMaxWidth);
      doc.text(lines, margin + 3, rowY + 1);

      // Amount - right aligned
      addTextRight(`INR ${formatIndianNumber(item.amount)}`, pageWidth - margin - 3, rowY + 1, 8, false);

      // Adjust yPos based on number of lines
      yPos += Math.max(rowHeight, lines.length * 4);
    });

    yPos += 5;
    drawLine(yPos);
    yPos += 5;

    // Total Earnings
    const totalEarnings = earnings.reduce((sum, item) => sum + item.amount, 0);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    addText('Total Earnings:', margin + 3, yPos + 1, 9, true);
    addTextRight(`INR ${formatIndianNumber(totalEarnings)}`, pageWidth - margin - 3, yPos + 1, 9, true);
    yPos += 8;

    // Deductions Section
    drawLine(yPos);
    yPos += 6;
    addText('Deductions', margin, yPos, 11, true, { r: 0, g: 0, b: 0 });
    yPos += 7;

    // Deductions Table Header
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.3);
    doc.setFillColor(248, 248, 248);
    doc.rect(margin, yPos - 3, pageWidth - 2 * margin, 6, 'FD');
    addText('Description', margin + 3, yPos + 1, 9, true, { r: 0, g: 0, b: 0 });
    addTextRight('Amount', pageWidth - margin - 3, yPos + 1, 9, true, { r: 0, g: 0, b: 0 });
    yPos += 8;

    // Deductions Table Rows
    const deductions = [
      { label: 'Advance Recovery', amount: settlement.components.deductions.advanceRecovery || 0 },
      { label: 'Loan Recovery', amount: settlement.components.deductions.loanRecovery || 0 },
      { label: 'Other Deductions', amount: settlement.components.deductions.otherDeductions || 0 },
    ];

    deductions.forEach((item, index) => {
      const rowHeight = 6;
      const rowY = yPos;

      // Subtle background for alternating rows
      if (index % 2 === 1) {
        doc.setFillColor(252, 252, 252);
        doc.rect(margin, rowY - 2, pageWidth - 2 * margin, rowHeight, 'F');
      }

      // Description with proper wrapping
      const descriptionMaxWidth = (pageWidth - 2 * margin) - 75;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(0, 0, 0);
      const lines = doc.splitTextToSize(item.label, descriptionMaxWidth);
      doc.text(lines, margin + 3, rowY + 1);

      // Amount - right aligned
      addTextRight(`INR ${formatIndianNumber(item.amount)}`, pageWidth - margin - 3, rowY + 1, 8, false);

      // Adjust yPos based on number of lines
      yPos += Math.max(rowHeight, lines.length * 4);
    });

    yPos += 5;
    drawLine(yPos);
    yPos += 5;

    // Total Deductions
    const totalDeductions = deductions.reduce((sum, item) => sum + item.amount, 0);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    addText('Total Deductions:', margin + 3, yPos + 1, 9, true);
    addTextRight(`INR ${formatIndianNumber(totalDeductions)}`, pageWidth - margin - 3, yPos + 1, 9, true);
    yPos += 8;

    // Net Payable Section - Highlighted with border
    const netPayable = settlement.netPayable || (totalEarnings - totalDeductions);

    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(1);
    doc.rect(margin, yPos - 2, pageWidth - 2 * margin, 18, 'D');
    doc.setFillColor(250, 250, 250);
    doc.rect(margin, yPos - 2, pageWidth - 2 * margin, 18, 'F');

    addTextCenter('NET PAYABLE AMOUNT', pageWidth / 2, yPos + 5, 10, true, { r: 0, g: 0, b: 0 });
    addTextCenter(`INR ${formatIndianNumber(netPayable)}`, pageWidth / 2, yPos + 12, 16, true, { r: 0, g: 0, b: 0 });
    yPos += 22;

    // Bank Details Section
    drawLine(yPos);
    yPos += 6;
    addText('Bank Details', margin, yPos, 11, true, { r: 0, g: 0, b: 0 });
    yPos += 7;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);

    // Use employee bank details if available, otherwise fall back to settlement bank details
    const b = settlement.bankDetails;
    const eb = settlement.employeeBankDetails;

    const isSettlementBankValid = b && b.bankName && b.bankName !== 'TBD' && b.bankName !== 'N/A';

    const bankName = isSettlementBankValid ? b.bankName : (eb?.bankName || 'N/A');
    const accountNumber = isSettlementBankValid ? b.accountNumber : (eb?.accountNumber || 'N/A');
    const ifscCode = isSettlementBankValid ? b.ifscCode : (eb?.ifscCode || 'N/A');
    const branchName = isSettlementBankValid ? (b.branchName || 'N/A') : (eb?.branchName || 'N/A');

    const bankData = [
      { label: 'Bank Name', value: bankName },
      { label: 'Branch Name', value: branchName },
      { label: 'Account Number', value: accountNumber },
      { label: 'IFSC Code', value: ifscCode },
    ];

    let bankDetailsYPos = yPos;
    bankData.forEach((item, index) => {
      const rowY = bankDetailsYPos;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text(`${item.label}:`, margin + 3, rowY);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(0, 0, 0);
      // Wrap bank details if they're too long
      const bankValueMaxWidth = pageWidth - margin - 50 - 3;
      const valueLines = doc.splitTextToSize(item.value, bankValueMaxWidth);
      doc.text(valueLines, margin + 50, rowY);
      // Adjust bankDetailsYPos if text wrapped, then add spacing for next item
      if (valueLines.length > 1) {
        bankDetailsYPos += (valueLines.length - 1) * 4;
      }
      bankDetailsYPos += 6; // Spacing for next bank detail item
    });

    yPos = bankDetailsYPos + 4; // Final spacing after bank details

    // Footer
    const footerY = pageHeight - 12;
    drawLine(footerY, 0.3);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(7);
    doc.setTextColor(140, 140, 140);
    addText('This is a system generated document. No signature required.', margin, footerY + 6, 7, false);
    addTextRight('Page 1 of 1', pageWidth - margin, footerY + 6, 7, false);

    // Save PDF
    doc.save(`settlement_${settlement.employeeId || 'unknown'}_${new Date().getTime()}.pdf`);
  };



  const mapSettlementsForExport = (settlements) => {
    return settlements.map(s => {
      // Determine which bank details to use
      // Prioritize settlement-specific bank details if they are valid (not TBD/NA)
      const b = s.bankDetails;
      const eb = s.employeeBankDetails;

      const isSettlementBankValid = b && b.bankName && b.bankName !== 'TBD' && b.bankName !== 'N/A';

      const bankName = isSettlementBankValid ? b.bankName : (eb?.bankName || '');
      const accountNumber = isSettlementBankValid ? b.accountNumber : (eb?.accountNumber || '');
      const ifscCode = isSettlementBankValid ? b.ifscCode : (eb?.ifscCode || '');

      return {
        'Employee Name': s.employeeName || '',
        'Employee ID': s.employeeId || '',
        'Department': s.department || '',
        'Status': s.status || '',
        'Resignation Date': formatDisplayDate(s.resignationDate),
        'Last Working Date': formatDisplayDate(s.lastWorkingDate),
        'Net Payable': s.netPayable || 0,
        'Salary Till LWD': s.components?.salaryTillLWD || 0,
        'Leave Encashment': s.components?.leaveEncashment || 0,
        'Bonus': s.components?.bonus || 0,
        'Gratuity': s.components?.gratuity || 0,
        'Other Allowances': s.components?.otherAllowances || 0,
        'Total Earnings': (
          (parseFloat(s.components?.salaryTillLWD) || 0) +
          (parseFloat(s.components?.leaveEncashment) || 0) +
          (parseFloat(s.components?.bonus) || 0) +
          (parseFloat(s.components?.gratuity) || 0) +
          (parseFloat(s.components?.otherAllowances) || 0)
        ),
        'Advance Recovery': s.components?.deductions?.advanceRecovery || 0,
        'Loan Recovery': s.components?.deductions?.loanRecovery || 0,
        'Other Deductions': s.components?.deductions?.otherDeductions || 0,
        'Total Deductions': (
          (parseFloat(s.components?.deductions?.advanceRecovery) || 0) +
          (parseFloat(s.components?.deductions?.loanRecovery) || 0) +
          (parseFloat(s.components?.deductions?.otherDeductions) || 0)
        ),
        'Bank Name': bankName,
        'Account Number': accountNumber,
        'IFSC Code': ifscCode,
        'Created Date': formatDisplayDate(s.createdAt, { includeTime: true })
      };
    });
  };

  const handleExportSelected = () => {
    const selectedData = filteredSettlements.filter(s => selectedRows.has(getSettlementId(s) || s.employeeId));
    const exportData = mapSettlementsForExport(selectedData);
    exportToCSV(exportData, 'settlements_selected', {
      title: 'Settlement List',
      type: 'Selected Rows'
    });
  };

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      handleExportSelected();
    } else {
      setShowExportModal(true);
    }
  };

  const handleExportCurrentPage = () => {
    try {
      const exportData = mapSettlementsForExport(paginatedSettlements);
      exportToCSV(exportData, 'settlements_current_page', {
        title: 'Settlement List',
        type: 'Current Page'
      });
      setShowExportModal(false);
      toast.success('Current page exported successfully');
    } catch (error) {
      toast.error('Failed to export settlements. Please try again.');
    }
  };

  const handleExportRange = async (startDate, endDate) => {
    setExportLoading(true);
    try {
      const queryParams = new URLSearchParams();
      if (startDate) queryParams.append("startDate", startDate);
      if (endDate) queryParams.append("endDate", endDate);
      if (searchTerm) queryParams.append("search", searchTerm);
      if (filterStatus !== 'all') queryParams.append("status", filterStatus);
      queryParams.append("type", `Date Range (${startDate} to ${endDate})`);

      // Construct URL - usually mounted under /api/payroll
      const url = `${API_BASE_URL}/payroll/settlements/export?${queryParams.toString()}`;
      const filename = generateExportFilename('settlements_range');
      await downloadFromBackend(url, filename);

      setShowExportModal(false);
      toast.success('Export started successfully');
    } catch (error) {
      toast.error("An error occurred during export");
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportAll = async () => {
    setExportLoading(true);
    try {
      const queryParams = new URLSearchParams();
      if (searchTerm) queryParams.append("search", searchTerm);
      if (filterStatus !== 'all') queryParams.append("status", filterStatus);
      if (startDate) queryParams.append("startDate", startDate);
      if (endDate) queryParams.append("endDate", endDate);
      queryParams.append("type", "All Data");

      const url = `${API_BASE_URL}/payroll/settlements/export?${queryParams.toString()}`;
      const filename = generateExportFilename('settlements_all');
      await downloadFromBackend(url, filename);

      setShowExportModal(false);
      toast.success('All data export started');
    } catch (error) {
      toast.error("An error occurred during export");
    } finally {
      setExportLoading(false);
    }
  };

  const isAmountFieldFilled = (value) => value !== null && value !== undefined && String(value).trim() !== '';

  const handleCreateSettlement = async () => {
    setFormSubmitted(true);
    // Validate all fields first
    if (!validateAllFields()) {
      // Mark all as touched
      const allTouched = {
        employeeId: true,
        lastWorkingDate: true,
        resignationDate: true,
        salaryTillLWD: true,
        leaveEncashment: true,
        bonus: true,
        gratuity: true,
        otherAllowances: true,
        advanceRecovery: true,
        loanRecovery: true,
        otherDeductions: true
      };
      setTouched(allTouched);
      toast.error('Please fill all required fields.');
      return;
    }

    const employeeId = (formData.employeeId || employeeIdRef.current?.value || '').trim();
    const lastWorkingDate = formData.lastWorkingDate || lastWorkingDateRef.current?.value || '';
    const resignationDate = formData.resignationDate || resignationDateRef.current?.value || '';

    const salaryTillLWD = formData.components.salaryTillLWD;
    const leaveEncashment = formData.components.leaveEncashment;
    const bonus = formData.components.bonus;
    const gratuity = formData.components.gratuity;
    const otherAllowances = formData.components.otherAllowances;
    const advanceRecovery = formData.components.deductions.advanceRecovery;
    const loanRecovery = formData.components.deductions.loanRecovery;
    const otherDeductions = formData.components.deductions.otherDeductions;

    const settlementData = {
      employeeId,
      lastWorkingDate,
      resignationDate,
      employeeName: selectedEmployeeDetails?.employeeName || employeeId,
      department: selectedEmployeeDetails?.department || 'Unknown',
      exitRequestId: selectedEmployeeDetails?.exitRequestId || undefined,
      components: {
        salaryTillLWD: parseFloat(salaryTillLWD) || 0,
        leaveEncashment: parseFloat(leaveEncashment) || 0,
        bonus: parseFloat(bonus) || 0,
        gratuity: parseFloat(gratuity) || 0,
        otherAllowances: parseFloat(otherAllowances) || 0,
        deductions: {
          advanceRecovery: parseFloat(advanceRecovery) || 0,
          loanRecovery: parseFloat(loanRecovery) || 0,
          otherDeductions: parseFloat(otherDeductions) || 0,
        },
      },
      bankDetails: {
        accountNumber: 'TBD',
        ifscCode: 'TBD',
        bankName: 'TBD',
      },
    };

    createSettlementMutation.mutate(settlementData);
  };

  const handleViewSettlement = (settlement) => {
    setSelectedSettlement(settlement);
    setShowSettlementModal(true);
  };

  const handleApproveClick = (settlement) => {
    setSettlementToApprove(settlement);
    setShowApproveModal(true);
  };

  const handleConfirmApprove = () => {
    if (!settlementToApprove) {
      toast.error('Unable to approve: missing settlement reference.');
      return;
    }
    const settlementId = getSettlementId(settlementToApprove);
    if (!settlementId) {
      toast.error('Unable to approve: invalid settlement ID.');
      return;
    }
    approveSettlementMutation.mutate(settlementId);
  };

  const handleCancelApprove = () => {
    setShowApproveModal(false);
    setSettlementToApprove(null);
  };

  const handleDeleteClick = (settlement) => {
    setSettlementToDelete(settlement);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (!settlementToDelete) {
      toast.error('Unable to delete: missing settlement reference.');
      return;
    }
    const settlementId = getSettlementId(settlementToDelete);
    if (!settlementId) {
      toast.error('Unable to delete: invalid settlement ID.');
      return;
    }
    deleteSettlementMutation.mutate(settlementId);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setSettlementToDelete(null);
  };

  const handleBulkDelete = async () => {
    if (selectedRows.size === 0) return;

    if (!window.confirm(`Are you sure you want to delete ${selectedRows.size} settlement(s)? This action cannot be undone.`)) return;

    setIsBulkDeleting(true);
    const idsToDelete = Array.from(selectedRows);
    let successCount = 0;
    let errorCount = 0;

    try {
      // Process deletions in parallel batches of 5 to speed up but not overwhelm the server
      const batchSize = 5;
      for (let i = 0; i < idsToDelete.length; i += batchSize) {
        const batch = idsToDelete.slice(i, i + batchSize);
        await Promise.all(batch.map(async (id) => {
          try {
            const response = await fullFinalSettlementApi.deleteSettlement(id);
            const statusCode = Number(response?.responseCode);
            if (Number.isFinite(statusCode) && [200, 201].includes(statusCode)) {
              successCount++;
            } else {
              errorCount++;
            }
          } catch (err) {
            errorCount++;
          }
        }));
      }

      if (successCount > 0) {
        toast.success(`Successfully deleted ${successCount} settlement(s)`);
        setSelectedRows(new Set());
        queryClient.invalidateQueries({ queryKey: ['fullFinalSettlements'] });
      }

      if (errorCount > 0) {
        toast.error(`Failed to delete ${errorCount} settlement(s). Please try again.`);
      }
    } finally {
      setIsBulkDeleting(false);
    }
  };

  // Fetch settlements
  const { data: settlements = [], isLoading: settlementsLoading, refetch } = useQuery({
    queryKey: ['fullFinalSettlements', filterStatus, searchTerm, startDate, endDate],
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (filterStatus !== 'all') queryParams.append('status', filterStatus);
      if (searchTerm) queryParams.append('search', searchTerm);
      if (startDate) queryParams.append('startDate', startDate);
      if (endDate) queryParams.append('endDate', endDate);

      const response = await fetch(`${API_BASE_URL}/payroll/settlements?${queryParams}`);
      if (!response.ok) {
        throw new Error('Failed to fetch settlements');
      }
      const data = await response.json();
      return data.data || [];
    },
    placeholderData: (prev) => prev,
    staleTime: 0,
    refetchInterval: 0,
  });

  // Filter logic is now handled by backend, so filteredSettlements is just the data
  // However, we might still want to keep the variable name to minimize refactoring impact
  const filteredSettlements = settlements;

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = paginatedSettlements.map(s => getSettlementId(s) || s.employeeId);
      setSelectedRows(new Set(allIds));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const sortedSettlements = useMemo(() => {
    if (!filteredSettlements) return [];
    if (!sortField) return filteredSettlements;

    return [...filteredSettlements].sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'employeeName':
          aValue = (a.employeeName || '').toLowerCase();
          bValue = (b.employeeName || '').toLowerCase();
          break;
        case 'department':
          aValue = (a.department || '').toLowerCase();
          bValue = (b.department || '').toLowerCase();
          break;
        case 'lastWorkingDate':
          aValue = new Date(a.lastWorkingDate || 0).getTime();
          bValue = new Date(b.lastWorkingDate || 0).getTime();
          break;
        case 'status':
          aValue = (a.status || '').toLowerCase();
          bValue = (b.status || '').toLowerCase();
          break;
        case 'netPayable':
          aValue = Number(a.netPayable || 0);
          bValue = Number(b.netPayable || 0);
          break;
        case 'createdAt':
          aValue = new Date(a.createdAt || 0).getTime();
          bValue = new Date(b.createdAt || 0).getTime();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredSettlements, sortField, sortOrder]);

  const settlementsTotalItems = sortedSettlements.length;
  const settlementsTotalPages = Math.max(1, Math.ceil(settlementsTotalItems / itemsPerPage));
  useEffect(() => {
    if (currentPage > settlementsTotalPages) {
      setCurrentPage(settlementsTotalPages);
    }
  }, [currentPage, settlementsTotalPages]);

  const paginatedSettlements = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedSettlements.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedSettlements, currentPage, itemsPerPage]);

  const getSettlementId = (settlement) => settlement?._id || settlement?.id || settlement?.settlementId || null;

  const renderCreateSettlementForm = () => {
    const totals = calculateTotal();

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Employee <span className="text-red-500">*</span>
            </label>
            <select
              ref={employeeIdRef}
              value={formData.employeeId}
              onChange={(e) => {
                const val = e.target.value;
                handleEmployeeSelection(val);
                if (touched.employeeId) {
                  const formErrors = validateSettlementForm({ ...formData, employeeId: val });
                  setErrors(prev => ({ ...prev, employeeId: formErrors.employeeId }));
                }
              }}
              onBlur={() => validateField('employeeId', formData.employeeId)}
              className={`input-field ${touched.employeeId && errors.employeeId ? 'border-red-500 bg-red-50' : ''
                }`}
            >
              <option value="">Select approved employee</option>
              {approvedEmployees.map((employee) => (
                <option key={employee.id} value={employee.employeeIdValue}>
                  {employee.displayLabel}
                </option>
              ))}
            </select>
            {touched.employeeId && errors.employeeId ? (
              <p className="text-xs text-red-500 mt-1">{errors.employeeId}</p>
            ) : isLoadingEmployees ? (
              <p className="text-xs text-gray-500 mt-1">Loading approved employees…</p>
            ) : approvedEmployees.length === 0 ? (
              <p className="text-xs text-red-500 mt-1">No approved exit employees available.</p>
            ) : selectedEmployeeDetails ? (
              <p className="text-xs text-gray-500 mt-1">
                {selectedEmployeeDetails.employeeName} • {selectedEmployeeDetails.department}
              </p>
            ) : (
              <p className="text-xs text-gray-500 mt-1">Choose an employee to auto-fill settlement details.</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Last Working Date <span className="text-red-500">*</span>
            </label>
            <input
              ref={lastWorkingDateRef}
              disabled
              type="date"
              defaultValue={formData.lastWorkingDate}
              onClick={(e) => e.target.showPicker && e.target.showPicker()}
              onBlur={() => validateField('lastWorkingDate', formData.lastWorkingDate)}
              className={`input-field ${touched.lastWorkingDate && errors.lastWorkingDate ? 'border-red-500 bg-red-50' : ''
                }`}
            />
            {touched.lastWorkingDate && errors.lastWorkingDate && (
              <p className="text-xs text-red-500 mt-1">{errors.lastWorkingDate}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Resignation Date <span className="text-red-500">*</span>
            </label>
            <input
              ref={resignationDateRef}
              disabled
              type="date"
              defaultValue={formData.resignationDate}
              onClick={(e) => e.target.showPicker && e.target.showPicker()}
              onBlur={() => validateField('resignationDate', formData.resignationDate)}
              className={`input-field ${touched.resignationDate && errors.resignationDate ? 'border-red-500 bg-red-50' : ''
                }`}
            />
            {touched.resignationDate && errors.resignationDate && (
              <p className="text-xs text-red-500 mt-1">{errors.resignationDate}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Earnings</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Salary till LWD <span className="text-red-500">*</span>
                </label>
                <input
                  ref={salaryTillLWDRef}
                  type="text"
                  inputMode="numeric"
                  value={formData.components.salaryTillLWD}
                  required
                  onChange={(e) => {
                    const sanitized = sanitizeNumericInput(e.target.value);
                    setFormData((prev) => ({
                      ...prev,
                      components: { ...prev.components, salaryTillLWD: sanitized },
                    }));
                    if (touched.salaryTillLWD) {
                      const formErrors = validateSettlementForm({
                        ...formData,
                        components: { ...formData.components, salaryTillLWD: sanitized }
                      });
                      setErrors(prev => ({ ...prev, salaryTillLWD: formErrors.salaryTillLWD }));
                    }
                  }}
                  onBlur={() => validateField('salaryTillLWD', formData.components.salaryTillLWD)}
                  onKeyDown={handleNumberKeyDown}
                  className={`input-field ${touched.salaryTillLWD && errors.salaryTillLWD ? 'border-red-500 bg-red-50' : ''
                    }`}
                  placeholder="25000"
                  autoComplete="off"
                />
                {touched.salaryTillLWD && errors.salaryTillLWD ? (
                  <p className="text-xs text-red-500 mt-1">{errors.salaryTillLWD}</p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">
                    {formatIndianNumber(formData.components.salaryTillLWD || 0)}/{formatIndianNumber(MAX_AMOUNT)} (max)
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Leave Encashment <span className="text-red-500">*</span>
                </label>
                <input
                  ref={leaveEncashmentRef}
                  type="text"
                  inputMode="numeric"
                  value={formData.components.leaveEncashment}
                  required
                  onChange={(e) => {
                    const sanitized = sanitizeNumericInput(e.target.value);
                    setFormData((prev) => ({
                      ...prev,
                      components: { ...prev.components, leaveEncashment: sanitized },
                    }));
                    if (touched.leaveEncashment) {
                      const formErrors = validateSettlementForm({
                        ...formData,
                        components: { ...formData.components, leaveEncashment: sanitized }
                      });
                      setErrors(prev => ({ ...prev, leaveEncashment: formErrors.leaveEncashment }));
                    }
                  }}
                  onBlur={() => validateField('leaveEncashment', formData.components.leaveEncashment)}
                  onKeyDown={handleNumberKeyDown}
                  className={`input-field ${touched.leaveEncashment && errors.leaveEncashment ? 'border-red-500 bg-red-50' : ''
                    }`}
                  placeholder="15000"
                  autoComplete="off"
                />
                {touched.leaveEncashment && errors.leaveEncashment ? (
                  <p className="text-xs text-red-500 mt-1">{errors.leaveEncashment}</p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">
                    {formatIndianNumber(formData.components.leaveEncashment || 0)}/{formatIndianNumber(MAX_AMOUNT)} (max)
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bonus <span className="text-red-500">*</span>
                </label>
                <input
                  ref={bonusRef}
                  type="text"
                  inputMode="numeric"
                  value={formData.components.bonus}
                  required
                  onChange={(e) => {
                    const sanitized = sanitizeNumericInput(e.target.value);
                    setFormData((prev) => ({
                      ...prev,
                      components: { ...prev.components, bonus: sanitized },
                    }));
                    if (touched.bonus) {
                      const formErrors = validateSettlementForm({
                        ...formData,
                        components: { ...formData.components, bonus: sanitized }
                      });
                      setErrors(prev => ({ ...prev, bonus: formErrors.bonus }));
                    }
                  }}
                  onBlur={() => validateField('bonus', formData.components.bonus)}
                  onKeyDown={handleNumberKeyDown}
                  className={`input-field ${touched.bonus && errors.bonus ? 'border-red-500 bg-red-50' : ''
                    }`}
                  placeholder="50000"
                  autoComplete="off"
                />
                {touched.bonus && errors.bonus ? (
                  <p className="text-xs text-red-500 mt-1">{errors.bonus}</p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">
                    {formatIndianNumber(formData.components.bonus || 0)}/{formatIndianNumber(MAX_AMOUNT)} (max)
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Gratuity <span className="text-red-500">*</span>
                </label>
                <input
                  ref={gratuityRef}
                  type="text"
                  inputMode="numeric"
                  value={formData.components.gratuity}
                  required
                  onChange={(e) => {
                    const sanitized = sanitizeNumericInput(e.target.value);
                    setFormData((prev) => ({
                      ...prev,
                      components: { ...prev.components, gratuity: sanitized },
                    }));
                    if (touched.gratuity) {
                      const formErrors = validateSettlementForm({
                        ...formData,
                        components: { ...formData.components, gratuity: sanitized }
                      });
                      setErrors(prev => ({ ...prev, gratuity: formErrors.gratuity }));
                    }
                  }}
                  onBlur={() => validateField('gratuity', formData.components.gratuity)}
                  onKeyDown={handleNumberKeyDown}
                  className={`input-field ${touched.gratuity && errors.gratuity ? 'border-red-500 bg-red-50' : ''
                    }`}
                  placeholder="100000"
                  autoComplete="off"
                />
                {touched.gratuity && errors.gratuity ? (
                  <p className="text-xs text-red-500 mt-1">{errors.gratuity}</p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">
                    {formatIndianNumber(formData.components.gratuity || 0)}/{formatIndianNumber(MAX_AMOUNT)} (max)
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Other Allowances <span className="text-red-500">*</span>
                </label>
                <input
                  ref={otherAllowancesRef}
                  type="text"
                  inputMode="numeric"
                  value={formData.components.otherAllowances}
                  required
                  onChange={(e) => {
                    const sanitized = sanitizeNumericInput(e.target.value);
                    setFormData((prev) => ({
                      ...prev,
                      components: { ...prev.components, otherAllowances: sanitized },
                    }));
                    if (touched.otherAllowances) {
                      const formErrors = validateSettlementForm({
                        ...formData,
                        components: { ...formData.components, otherAllowances: sanitized }
                      });
                      setErrors(prev => ({ ...prev, otherAllowances: formErrors.otherAllowances }));
                    }
                  }}
                  onBlur={() => validateField('otherAllowances', formData.components.otherAllowances)}
                  onKeyDown={handleNumberKeyDown}
                  className={`input-field ${touched.otherAllowances && errors.otherAllowances ? 'border-red-500 bg-red-50' : ''
                    }`}
                  placeholder="10000"
                  autoComplete="off"
                />
                {touched.otherAllowances && errors.otherAllowances ? (
                  <p className="text-xs text-red-500 mt-1">{errors.otherAllowances}</p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">
                    {formatIndianNumber(formData.components.otherAllowances || 0)}/{formatIndianNumber(MAX_AMOUNT)} (max)
                  </p>
                )}
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Deductions</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Advance Recovery <span className="text-red-500">*</span>
                </label>
                <input
                  ref={advanceRecoveryRef}
                  type="text"
                  inputMode="numeric"
                  value={formData.components.deductions.advanceRecovery}
                  required
                  onChange={(e) => {
                    const sanitized = sanitizeNumericInput(e.target.value);
                    setFormData((prev) => ({
                      ...prev,
                      components: {
                        ...prev.components,
                        deductions: { ...prev.components.deductions, advanceRecovery: sanitized },
                      },
                    }));
                    if (touched.advanceRecovery) {
                      const formErrors = validateSettlementForm({
                        ...formData,
                        components: {
                          ...formData.components,
                          deductions: { ...formData.components.deductions, advanceRecovery: sanitized }
                        }
                      });
                      setErrors(prev => ({ ...prev, advanceRecovery: formErrors.advanceRecovery }));
                    }
                  }}
                  onBlur={() => validateField('advanceRecovery', formData.components.deductions.advanceRecovery)}
                  onKeyDown={handleNumberKeyDown}
                  className={`input-field ${touched.advanceRecovery && errors.advanceRecovery ? 'border-red-500 bg-red-50' : ''
                    }`}
                  placeholder="5000"
                  autoComplete="off"
                />
                {touched.advanceRecovery && errors.advanceRecovery ? (
                  <p className="text-xs text-red-500 mt-1">{errors.advanceRecovery}</p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">
                    {formatIndianNumber(formData.components.deductions.advanceRecovery || 0)}/{formatIndianNumber(MAX_AMOUNT)} (max)
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Loan Recovery <span className="text-red-500">*</span>
                </label>
                <input
                  ref={loanRecoveryRef}
                  type="text"
                  inputMode="numeric"
                  value={formData.components.deductions.loanRecovery}
                  required
                  onChange={(e) => {
                    const sanitized = sanitizeNumericInput(e.target.value);
                    setFormData((prev) => ({
                      ...prev,
                      components: {
                        ...prev.components,
                        deductions: { ...prev.components.deductions, loanRecovery: sanitized },
                      },
                    }));
                    if (touched.loanRecovery) {
                      const formErrors = validateSettlementForm({
                        ...formData,
                        components: {
                          ...formData.components,
                          deductions: { ...formData.components.deductions, loanRecovery: sanitized }
                        }
                      });
                      setErrors(prev => ({ ...prev, loanRecovery: formErrors.loanRecovery }));
                    }
                  }}
                  onBlur={() => validateField('loanRecovery', formData.components.deductions.loanRecovery)}
                  onKeyDown={handleNumberKeyDown}
                  className={`input-field ${touched.loanRecovery && errors.loanRecovery ? 'border-red-500 bg-red-50' : ''
                    }`}
                  placeholder="25000"
                  autoComplete="off"
                />
                {touched.loanRecovery && errors.loanRecovery ? (
                  <p className="text-xs text-red-500 mt-1">{errors.loanRecovery}</p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">
                    {formatIndianNumber(formData.components.deductions.loanRecovery || 0)}/{formatIndianNumber(MAX_AMOUNT)} (max)
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Other Deductions <span className="text-red-500">*</span>
                </label>
                <input
                  ref={otherDeductionsRef}
                  type="text"
                  inputMode="numeric"
                  value={formData.components.deductions.otherDeductions}
                  required
                  onChange={(e) => {
                    const sanitized = sanitizeNumericInput(e.target.value);
                    setFormData((prev) => ({
                      ...prev,
                      components: {
                        ...prev.components,
                        deductions: { ...prev.components.deductions, otherDeductions: sanitized },
                      },
                    }));
                    if (touched.otherDeductions) {
                      const formErrors = validateSettlementForm({
                        ...formData,
                        components: {
                          ...formData.components,
                          deductions: { ...formData.components.deductions, otherDeductions: sanitized }
                        }
                      });
                      setErrors(prev => ({ ...prev, otherDeductions: formErrors.otherDeductions }));
                    }
                  }}
                  onBlur={() => validateField('otherDeductions', formData.components.deductions.otherDeductions)}
                  onKeyDown={handleNumberKeyDown}
                  className={`input-field ${touched.otherDeductions && errors.otherDeductions ? 'border-red-500 bg-red-50' : ''
                    }`}
                  placeholder="2000"
                  autoComplete="off"
                />
                {touched.otherDeductions && errors.otherDeductions ? (
                  <p className="text-xs text-red-500 mt-1">{errors.otherDeductions}</p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">
                    {formatIndianNumber(formData.components.deductions.otherDeductions || 0)}/{formatIndianNumber(MAX_AMOUNT)} (max)
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">Settlement Summary</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-sm text-gray-600">Total Earnings</p>
              <p className="text-xl font-bold text-green-600">₹{formatIndianNumber(totals.earnings)}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600">Total Deductions</p>
              <p className="text-xl font-bold text-red-600">₹{formatIndianNumber(totals.deductions)}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600">Net Payable</p>
              <p className="text-xl font-bold text-brand">₹{formatIndianNumber(totals.netPayable)}</p>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <Button
            className="btn-cancel"
            onClick={() => setShowSettlementModal(false)}
          >
            Cancel
          </Button>
          <PermissionGate permission="payroll" action="create">
            <Button
              className="btn-create flex items-center"
              icon={<Plus className="h-4 w-4" />}
              onClick={handleCreateSettlement}
              disabled={isCreatingSettlement}
            >
              {isCreatingSettlement ? 'Creating...' : 'Create Settlement'}
            </Button>
          </PermissionGate>
        </div>
      </div>
    );
  };

  const renderSettlementModal = () => {
    const totals = calculateTotal();

    return (
      <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto mx-4 my-4">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 break-words">
                {selectedSettlement ? 'Settlement Details' : 'Create Full & Final Settlement'}
              </h2>
              {!selectedSettlement && (
                <button
                  onClick={() => {
                    setShowSettlementModal(false);
                    setSelectedSettlement(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              )}
            </div>

            {selectedSettlement ? (
              <div className="space-y-6">
                {/* Action buttons at the top */}
                <div className="flex justify-end space-x-3 pb-4 border-b border-gray-200">
                  {selectedSettlement.status === 'approved' && (
                    <PermissionGate permission="payroll" action="read">
                      <Button
                        className="btn-cancel flex items-center"
                        icon={<Download className="h-4 w-4" />}
                        onClick={() => handleDownloadSettlement(selectedSettlement)}
                      >
                        Download Settlement
                      </Button>
                    </PermissionGate>
                  )}
                  {selectedSettlement.status === 'pending' && (
                    <PermissionGate permission="payroll" action="update">
                      <Button
                        className="btn-create flex items-center"
                        icon={<CheckCircle className="h-4 w-4" />}
                        onClick={() => handleApproveClick(selectedSettlement)}
                        disabled={isApprovingSettlement}
                      >
                        {isApprovingSettlement ? 'Approving...' : 'Approve Settlement'}
                      </Button>
                    </PermissionGate>
                  )}
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Employee Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Employee Name</p>
                      <p className="font-medium break-words">{selectedSettlement.employeeName || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Employee ID</p>
                      <p className="font-medium break-words">{selectedSettlement.employeeId || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Department</p>
                      <p className="font-medium break-words">{selectedSettlement.department || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Last Working Date</p>
                      <p className="font-medium break-words">{formatDisplayDate(selectedSettlement.lastWorkingDate)}</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white border border-gray-200 rounded-lg">
                    <div className="p-4 border-b border-gray-200">
                      <h4 className="text-lg font-semibold text-gray-900">Earnings</h4>
                    </div>
                    <div className="p-4">
                      <div className="space-y-3">
                        <div className="flex justify-between items-start gap-2">
                          <span className="text-gray-700 break-words flex-1 min-w-0">Salary till LWD</span>
                          <span className="font-medium text-green-600 whitespace-nowrap ml-2">₹{(selectedSettlement.components.salaryTillLWD || 0).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between items-start gap-2">
                          <span className="text-gray-700 break-words flex-1 min-w-0">Leave Encashment</span>
                          <span className="font-medium text-green-600 whitespace-nowrap ml-2">₹{(selectedSettlement.components.leaveEncashment || 0).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between items-start gap-2">
                          <span className="text-gray-700 break-words flex-1 min-w-0">Bonus</span>
                          <span className="font-medium text-green-600 whitespace-nowrap ml-2">₹{(selectedSettlement.components.bonus || 0).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between items-start gap-2">
                          <span className="text-gray-700 break-words flex-1 min-w-0">Gratuity</span>
                          <span className="font-medium text-green-600 whitespace-nowrap ml-2">₹{(selectedSettlement.components.gratuity || 0).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between items-start gap-2">
                          <span className="text-gray-700 break-words flex-1 min-w-0">Other Allowances</span>
                          <span className="font-medium text-green-600 whitespace-nowrap ml-2">₹{(selectedSettlement.components.otherAllowances || 0).toLocaleString()}</span>
                        </div>
                      </div>
                      <div className="border-t pt-3 mt-3">
                        <div className="flex justify-between">
                          <span className="font-semibold text-gray-900">Total Earnings</span>
                          <span className="font-bold text-green-600">
                            ₹{(
                              (selectedSettlement.components.salaryTillLWD || 0) +
                              (selectedSettlement.components.leaveEncashment || 0) +
                              (selectedSettlement.components.bonus || 0) +
                              (selectedSettlement.components.gratuity || 0) +
                              (selectedSettlement.components.otherAllowances || 0)
                            ).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg">
                    <div className="p-4 border-b border-gray-200">
                      <h4 className="text-lg font-semibold text-gray-900">Deductions</h4>
                    </div>
                    <div className="p-4">
                      <div className="space-y-3">
                        <div className="flex justify-between items-start gap-2">
                          <span className="text-gray-700 break-words flex-1 min-w-0">Advance Recovery</span>
                          <span className="font-medium text-red-600 whitespace-nowrap ml-2">₹{(selectedSettlement.components.deductions.advanceRecovery || 0).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between items-start gap-2">
                          <span className="text-gray-700 break-words flex-1 min-w-0">Loan Recovery</span>
                          <span className="font-medium text-red-600 whitespace-nowrap ml-2">₹{(selectedSettlement.components.deductions.loanRecovery || 0).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between items-start gap-2">
                          <span className="text-gray-700 break-words flex-1 min-w-0">Other Deductions</span>
                          <span className="font-medium text-red-600 whitespace-nowrap ml-2">₹{(selectedSettlement.components.deductions.otherDeductions || 0).toLocaleString()}</span>
                        </div>
                      </div>
                      <div className="border-t pt-3 mt-3">
                        <div className="flex justify-between">
                          <span className="font-semibold text-gray-900">Total Deductions</span>
                          <span className="font-bold text-red-600">
                            ₹{(
                              (selectedSettlement.components.deductions.advanceRecovery || 0) +
                              (selectedSettlement.components.deductions.loanRecovery || 0) +
                              (selectedSettlement.components.deductions.otherDeductions || 0)
                            ).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-brand to-blue-500 rounded-lg p-6 text-white">
                  <div className="text-center">
                    <h3 className="text-xl font-bold mb-2">Net Payable Amount</h3>
                    <p className="text-3xl font-bold">₹{(selectedSettlement.netPayable || 0).toLocaleString()}</p>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Bank Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Bank Name</p>
                      <p className="font-medium break-words capitalize">
                        {(() => {
                          const b = selectedSettlement.bankDetails;
                          const eb = selectedSettlement.employeeBankDetails;
                          const isValid = b && b.bankName && b.bankName !== 'TBD' && b.bankName !== 'N/A';
                          return isValid ? b.bankName : (eb?.bankName || 'N/A');
                        })()}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Account Number</p>
                      <p className="font-medium break-words">
                        {(() => {
                          const b = selectedSettlement.bankDetails;
                          const eb = selectedSettlement.employeeBankDetails;
                          const isValid = b && b.bankName && b.bankName !== 'TBD' && b.bankName !== 'N/A';
                          return isValid ? b.accountNumber : (eb?.accountNumber || 'N/A');
                        })()}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">IFSC Code</p>
                      <p className="font-medium break-words capitalize">
                        {(() => {
                          const b = selectedSettlement.bankDetails;
                          const eb = selectedSettlement.employeeBankDetails;
                          const isValid = b && b.bankName && b.bankName !== 'TBD' && b.bankName !== 'N/A';
                          return isValid ? b.ifscCode : (eb?.ifscCode || 'N/A');
                        })()}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Branch Name</p>
                      <p className="font-medium break-words capitalize">
                        {(() => {
                          const b = selectedSettlement.bankDetails;
                          const eb = selectedSettlement.employeeBankDetails;
                          const isValid = b && b.bankName && b.bankName !== 'TBD' && b.bankName !== 'N/A';
                          return isValid ? (b.branchName || 'N/A') : (eb?.branchName || 'N/A');
                        })()}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Close button at the bottom */}
                <div className="flex justify-end pt-6 border-t border-gray-200">
                  <Button
                    className="btn-cancel"
                    onClick={() => {
                      setShowSettlementModal(false);
                      setSelectedSettlement(null);
                    }}
                  >
                    Close Window
                  </Button>
                </div>
              </div>
            ) : (
              renderCreateSettlementForm()
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Full & Final Settlement</h1>
          <p className="text-gray-600 mt-1">Process full and final settlements for exiting employees</p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center space-x-4">
          <PermissionGate permission="payroll" action="delete">
            {selectedRows.size > 0 && (
              <Button
                variant="danger"
                onClick={handleBulkDelete}
                disabled={isBulkDeleting}
                icon={isBulkDeleting ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> : <Trash2 className="h-4 w-4" />}
                className="bg-red-600 hover:bg-red-700 text-white border-none"
              >
                Delete Selected ({selectedRows.size})
              </Button>
            )}
          </PermissionGate>
          <PermissionGate permission="payroll" action="read">
            <Button
              variant="outline"
              icon={exportLoading ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-brand border-t-transparent" /> : <Download className="h-4 w-4" />}
              onClick={handleExportClick}
              disabled={exportLoading}
              className={selectedRows.size > 0 ? "bg-brand/5 border-brand/20 text-brand" : "btn-outline"}
            >
              {selectedRows.size > 0 ? `Export Selected Records (${selectedRows.size})` : "Export Records"}
            </Button>
          </PermissionGate>
          <PermissionGate permission="payroll" action="create">
            <Button
              variant="outline"
              icon={<Upload className="h-4 w-4" />}
              onClick={() => setShowBulkUpload(true)}
              className="btn-outline border-brand/50 text-brand hover:bg-brand/5"
            >
              Bulk Upload
            </Button>
          </PermissionGate>
          <PermissionGate permission="payroll" action="create">
            <Button
              className="btn-cancel flex items-center"
              icon={<Plus className="h-4 w-4" />}
              onClick={() => {
                setSelectedSettlement(null);
                setShowSettlementModal(true);
              }}
            >
              Create Settlement
            </Button>
          </PermissionGate>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-end gap-4 w-full">
          <div className="relative flex-[2] min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by Name, Email or Department..."
              value={searchTerm}
              onChange={(e) => {
                // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                setSearchTerm(value);
              }}
              onKeyPress={(e) => {
                // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                if (!/^[a-zA-Z0-9\s@._+-]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              maxLength={100}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-brand/60"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm('');
                  const input = document.querySelector('input[placeholder="Search settlements..."]');
                  if (input) input.focus();
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="flex-shrink-0">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-brand/60"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
            </select>
          </div>
          <div className="flex-shrink-0">
            <DateRangeFilter
              startDate={startDate}
              endDate={endDate}
              onChange={setDateRange}
              containerClassName="flex items-center space-x-2"
              inputClassName="text-sm w-36 cursor-pointer px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-brand/60"
            />
          </div>
          {(searchTerm || filterStatus !== 'all' || startDate || endDate) && (
            <div className="relative flex-shrink-0">
              <Button
                variant="outline"
                icon={<X className="h-4 w-4" />}
                onClick={() => {
                  resetSettlementFilters();
                }}
              >
                Clear
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-transparent" style={{ backgroundColor: "rgba(59, 130, 246, 0.14)" }}>
              <tr>
                <th className="px-6 py-3 text-left w-10">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-brand focus:ring-brand h-4 w-4"
                    checked={paginatedSettlements.length > 0 && paginatedSettlements.every((settlement) => selectedRows.has(getSettlementId(settlement) || settlement.employeeId))}
                    onChange={handleSelectAll}
                  />
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                  onClick={() => handleSort('employeeName')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Employee</span>
                    {sortField === 'employeeName' ? (
                      sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 flex-shrink-0 text-brand" /> : <ArrowDown className="w-3 h-3 flex-shrink-0 text-brand" />
                    ) : <ArrowUpDown className="w-3 h-3 flex-shrink-0 text-gray-400" />}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                  onClick={() => handleSort('department')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Department</span>
                    {sortField === 'department' ? (
                      sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 flex-shrink-0 text-brand" /> : <ArrowDown className="w-3 h-3 flex-shrink-0 text-brand" />
                    ) : <ArrowUpDown className="w-3 h-3 flex-shrink-0 text-gray-400" />}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                  onClick={() => handleSort('lastWorkingDate')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Last Working Date</span>
                    {sortField === 'lastWorkingDate' ? (
                      sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 flex-shrink-0 text-brand" /> : <ArrowDown className="w-3 h-3 flex-shrink-0 text-brand" />
                    ) : <ArrowUpDown className="w-3 h-3 flex-shrink-0 text-gray-400" />}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Status</span>
                    {sortField === 'status' ? (
                      sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 flex-shrink-0 text-brand" /> : <ArrowDown className="w-3 h-3 flex-shrink-0 text-brand" />
                    ) : <ArrowUpDown className="w-3 h-3 flex-shrink-0 text-gray-400" />}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                  onClick={() => handleSort('netPayable')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Net Payable</span>
                    {sortField === 'netPayable' ? (
                      sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 flex-shrink-0 text-brand" /> : <ArrowDown className="w-3 h-3 flex-shrink-0 text-brand" />
                    ) : <ArrowUpDown className="w-3 h-3 flex-shrink-0 text-gray-400" />}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                  onClick={() => handleSort('createdAt')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Created At</span>
                    {sortField === 'createdAt' ? (
                      sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 flex-shrink-0 text-brand" /> : <ArrowDown className="w-3 h-3 flex-shrink-0 text-brand" />
                    ) : <ArrowUpDown className="w-3 h-3 flex-shrink-0 text-gray-400" />}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {settlementsLoading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-8 text-center text-sm text-gray-500">
                    Loading settlements...
                  </td>
                </tr>
              ) : (
                paginatedSettlements.map((settlement) => (
                  <tr key={getSettlementId(settlement) || settlement.employeeId} className={`hover:bg-blue-50/30 ${selectedRows.has(getSettlementId(settlement) || settlement.employeeId) ? 'bg-blue-50/50' : ''}`}>
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-brand focus:ring-brand h-4 w-4"
                        checked={selectedRows.has(getSettlementId(settlement) || settlement.employeeId)}
                        onChange={() => handleSelectRow(getSettlementId(settlement) || settlement.employeeId)}
                      />
                    </td>
                    <td className="px-6 py-4 max-w-[200px]" title={`${settlement.employeeName || 'N/A'}\n${settlement.employeeEmail || 'N/A'}`}>
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8">
                          <div className="h-8 w-8 rounded-full bg-gradient-to-br from-green-400 to-blue-500 flex items-center justify-center">
                            <User className="h-4 w-4 text-white" />
                          </div>
                        </div>
                        <div className="ml-4 min-w-0 flex-1 text-sm">
                          <div className="font-medium text-gray-900 truncate">
                            {settlement.employeeName || 'N/A'}
                          </div>
                          <div className="text-gray-500 truncate">
                            {settlement.employeeEmail || 'N/A'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 max-w-[150px]">
                      <div className="text-sm text-gray-900 truncate" title={settlement.department || 'N/A'}>
                        {settlement.department || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDisplayDate(settlement.lastWorkingDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full capitalize ${getStatusColor(settlement.status)}`}>
                        {settlement.status || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ₹{(settlement.netPayable || 0).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDisplayDate(settlement.createdAt, { includeTime: true })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <PermissionGate permission="payroll" action="read">
                          <button
                            onClick={() => handleViewSettlement(settlement)}
                            className="text-brand hover:text-brand/90"
                            title="View details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        </PermissionGate>
                        {settlement.status === 'approved' && (
                          <PermissionGate permission="payroll" action="read">
                            <button
                              onClick={() => handleDownloadSettlement(settlement)}
                              className="text-purple-600 hover:text-purple-900"
                              title="Download PDF"
                            >
                              <Download className="h-4 w-4" />
                            </button>
                          </PermissionGate>
                        )}
                        {settlement.status === 'pending' && (
                          <PermissionGate permission="payroll" action="update">
                            <button
                              onClick={() => handleApproveClick(settlement)}
                              className={`text-green-600 hover:text-green-900 ${isApprovingSettlement ? 'opacity-50 cursor-not-allowed' : ''}`}
                              disabled={isApprovingSettlement}
                              title="Approve settlement"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </button>
                          </PermissionGate>
                        )}
                        <PermissionGate permission="payroll" action="delete">
                          <button
                            onClick={() => handleDeleteClick(settlement)}
                            className={`text-red-600 hover:text-red-900 ${isDeletingSettlement ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={isDeletingSettlement}
                            title="Delete settlement"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </PermissionGate>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          {!settlementsLoading && settlementsTotalItems > 0 && (
            <PaginationControls
              currentPage={currentPage}
              totalPages={settlementsTotalPages}
              totalItems={settlementsTotalItems}
              limit={itemsPerPage}
              hasNextPage={currentPage < settlementsTotalPages}
              hasPrevPage={currentPage > 1}
              onPageChange={setCurrentPage}
              onLimitChange={(limit) => {
                setItemsPerPage(limit);
              }}
              limitOptions={[10, 25, 50, 100]}
              border={false}
            />
          )}
        </div>
      </div>

      {
        filteredSettlements.length === 0 && !settlementsLoading && (
          <div className="text-center py-12">
            <Receipt className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No settlements found</h3>
            <p className="text-gray-500 mb-4">
              {searchTerm || filterStatus !== 'all'
                ? 'Try adjusting your search or filter criteria.'
                : 'Create your first full & final settlement to get started.'}
            </p>
            {!searchTerm && filterStatus === 'all' && (
              <PermissionGate permission="payroll" action="create">
                <Button
                  className="btn-brand-light"
                  icon={<Plus className="h-4 w-4" />}
                  onClick={() => setShowSettlementModal(true)}
                >
                  Create Settlement
                </Button>
              </PermissionGate>
            )}
          </div>
        )
      }

      {showSettlementModal && renderSettlementModal()}

      {/* Approve Confirmation Modal */}
      {
        showApproveModal && settlementToApprove && (
          <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="p-6">
                <div className="flex items-center justify-center mb-4">
                  <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                  Approve Settlement
                </h3>
                <p className="text-sm text-gray-600 text-center mb-4">
                  Are you sure you want to approve the settlement for{' '}
                  <span className="font-semibold text-gray-900 break-words">
                    {settlementToApprove.employeeName || settlementToApprove.employeeId || 'this employee'}
                  </span>?
                  <br />
                  <span className="text-green-600 font-medium">This will mark the settlement as approved.</span>
                </p>
                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                  <div className="text-xs text-gray-600 space-y-1">
                    <div className="flex justify-between">
                      <span>Employee ID:</span>
                      <span className="font-medium">{settlementToApprove.employeeId || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Net Payable:</span>
                      <span className="font-medium">₹{formatIndianNumber(settlementToApprove.netPayable || 0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Status:</span>
                      <span className="font-medium capitalize">{settlementToApprove.status || 'N/A'}</span>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end space-x-3">
                  <Button
                    className="btn-cancel"
                    onClick={handleCancelApprove}
                    disabled={isApprovingSettlement}
                  >
                    Discard Changes
                  </Button>
                  <PermissionGate permission="payroll" action="update">
                    <Button
                      className="btn-create flex items-center"
                      onClick={handleConfirmApprove}
                      disabled={isApprovingSettlement}
                      icon={<CheckCircle className="h-4 w-4" />}
                    >
                      {isApprovingSettlement ? 'Approving...' : 'Approve Settlement'}
                    </Button>
                  </PermissionGate>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Delete Confirmation Modal */}
      {
        showDeleteModal && settlementToDelete && (
          <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="p-6">
                <div className="flex items-center justify-center mb-4">
                  <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                    <AlertTriangle className="h-6 w-6 text-red-600" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                  Delete Settlement
                </h3>
                <p className="text-sm text-gray-600 text-center mb-4">
                  Are you sure you want to delete the settlement for{' '}
                  <span className="font-semibold text-gray-900 break-words">
                    {settlementToDelete.employeeName || settlementToDelete.employeeId || 'this employee'}
                  </span>?
                  <br />
                  <span className="text-red-600 font-medium">This action cannot be undone.</span>
                </p>
                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                  <div className="text-xs text-gray-600 space-y-1">
                    <div className="flex justify-between">
                      <span>Employee ID:</span>
                      <span className="font-medium">{settlementToDelete.employeeId || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Net Payable:</span>
                      <span className="font-medium">₹{(settlementToDelete.netPayable || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Status:</span>
                      <span className="font-medium capitalize">{settlementToDelete.status || 'N/A'}</span>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end space-x-3">
                  <Button
                    className="btn-cancel"
                    onClick={handleCancelDelete}
                    disabled={isDeletingSettlement}
                  >
                    Cancel
                  </Button>
                  <PermissionGate permission="payroll" action="delete">
                    <Button
                      className="bg-red-500 hover:bg-red-600 text-white border-none flex items-center"
                      onClick={handleConfirmDelete}
                      disabled={isDeletingSettlement}
                      icon={<Trash2 className="h-4 w-4" />}
                    >
                      {isDeletingSettlement ? 'Deleting...' : 'Delete Record'}
                    </Button>
                  </PermissionGate>
                </div>
              </div>
            </div>
          </div>
        )
      }
      {
        showExportModal && (
          <ExportModal
            isOpen={showExportModal}
            onClose={() => setShowExportModal(false)}
            onExportCurrentPage={handleExportCurrentPage}
            onExportRange={handleExportRange}
            onExportAll={handleExportAll}
            loading={exportLoading}
            title="Export Settlements"
          />
        )
      }
      {
        showBulkUpload && (
          <BulkUploadModal
            isOpen={showBulkUpload}
            onClose={() => setShowBulkUpload(false)}
            onSuccess={() => queryClient.invalidateQueries({ queryKey: ['fullFinalSettlements'] })}
            config={{
              title: "Bulk Upload Settlements",
              subtitle: "Import multiple full & final settlements using a CSV file",
              templateEndpoint: "/payroll/settlements/bulk-upload/template",
              validateEndpoint: "/payroll/settlements/bulk-upload/validate",
              uploadEndpoint: "/payroll/settlements/bulk-upload",
              templateFilename: "ff_settlement_bulk_upload_template.csv",
              dataKey: "data",
              maxRecords: 100,
              instructions: [
                "Employee ID is required and must correspond to an active employee.",
                "Employee Name and Department are required.",
                "Resignation Date and Last Working Date are required (YYYY-MM-DD).",
                "Financial components (Salary, Bonus, etc.) should be numbers.",
                "Bank details are optional (will default to 'TBD' if not provided).",
                "Remarks are optional."
              ]
            }}
          />
        )
      }
    </div>
  );
};

export default FullFinalSettlement;
